import { useState, useEffect } from 'react';
import { ExternalLink, Shield, Loader2, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  active: 'bg-status-up/10 text-status-up border-status-up/20',
  staging: 'bg-status-degraded/10 text-status-degraded border-status-degraded/20',
  planned: 'bg-muted/20 text-muted-foreground border-white/10',
  unavailable: 'bg-status-down/10 text-status-down border-status-down/20',
};

function LaunchpadStatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.planned;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono border ${style}`}>
      {(status || 'unknown').toUpperCase()}
    </span>
  );
}

function normalizeApps(data) {
  if (!Array.isArray(data)) return [];
  return data.filter((app) => app && typeof app === 'object' && app.id);
}

function LaunchpadUnavailable({ message }) {
  return (
    <section
      className="glass rounded-xl border border-status-degraded/30 p-5"
      data-testid="super-admin-launchpad-unavailable"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-status-degraded flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="font-heading font-bold text-foreground">Platform launchpad unavailable</h2>
          <p className="text-xs text-muted-foreground mt-1">{message}</p>
        </div>
      </div>
    </section>
  );
}

export default function SuperAdminLaunchpad() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get('/launchpad/apps');
        if (cancelled) return;
        const list = normalizeApps(resp?.data);
        if (!Array.isArray(resp?.data) && resp?.data?.detail) {
          setError('Could not load launchpad apps. Please refresh or try again later.');
          setApps([]);
          return;
        }
        setApps(list);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 403) {
          setError(null);
          setApps([]);
        } else if (status === 401) {
          setError('Session expired or not authorized for the launchpad.');
        } else {
          setError('Could not load launchpad apps. Please refresh or try again later.');
          if (status !== 401 && status !== 403) {
            toast.error('Failed to load platform launchpad');
          }
        }
        setApps([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openApp = async (app) => {
    if (!app?.id) return;
    setOpeningId(app.id);
    try {
      const resp = await api.post(`/launchpad/apps/${app.id}/open`);
      const { url, mode } = resp?.data || {};
      if (!url) {
        toast.error(`Could not open ${app.app_name || 'app'}`);
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      if (mode === 'sso_handoff') {
        toast.success(`Secure handoff opened for ${app.app_name}`);
      } else {
        toast.message(`Opened ${app.app_name} admin login`, {
          description: 'Sign in on the target app — credentials stay on that server, not in Mayr Ops.',
        });
      }
    } catch {
      toast.error(`Could not open ${app.app_name || 'app'}`);
    } finally {
      setOpeningId(null);
    }
  };

  if (loading) {
    return (
      <div className="glass rounded-xl border border-primary/20 p-6 flex items-center justify-center h-32">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <LaunchpadUnavailable message={error} />;
  }

  if (apps.length === 0) {
    return null;
  }

  return (
    <section className="glass rounded-xl border border-primary/20 p-5" data-testid="super-admin-launchpad">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
          <Shield className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-foreground">Platform launchpad</h2>
          <p className="text-xs text-muted-foreground">
            Open admin panels on connected apps from your Mayr Ops account — handoff tokens come from the server only.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {apps.map((app) => (
          <article
            key={app.id}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-col gap-3 hover:border-primary/30 transition-colors"
            data-testid={`launchpad-card-${app.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-heading font-semibold text-foreground">{app.app_name || app.id}</h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{app.description || '—'}</p>
              </div>
              <LaunchpadStatusBadge status={app.status} />
            </div>

            <div className="text-xs font-mono text-muted-foreground truncate" title={app.production_url || ''}>
              {app.production_url || '—'}
            </div>

            <div className="text-xs text-muted-foreground">
              {app.access_mode === 'sso_handoff'
                ? 'SSO handoff — server-minted token'
                : 'Direct admin link — manual sign-in on target app'}
            </div>

            <button
              type="button"
              onClick={() => openApp(app)}
              disabled={openingId === app.id}
              className="mt-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary/15 border border-primary/30 text-primary text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50"
              data-testid={`launchpad-open-${app.id}`}
            >
              {openingId === app.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              Open admin panel
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}
